import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  CreateDesktopLicenseRequest,
  DesktopLicenseWithProduct,
  LicensesService,
  NewDesktopLicenseDetails,
} from 'src/app/services/licenses.service';
import {
  Product,
  ProductVariant,
  ProductsService,
} from 'src/app/services/products.service';
import { Tenant } from 'src/app/services/tenants.service';

export type LicenseAddIntent = 'primary' | 'addon';

export type WizardStep =
  | 'intent'
  | 'site-license-choice'
  | 'parent-select'
  | 'product-select'
  | 'license-config'
  | 'more-addons';

type QueuedLicense = {
  product: Product;
  variant: ProductVariant;
  isSiteLicense: boolean;
  isNewLicense: boolean;
  startDate: string;
  expirationDate: string;
};

@Component({
  selector: 'app-add-license',
  templateUrl: './add-license.component.html',
  styleUrl: './add-license.component.scss',
})
export class AddLicenseComponent {
  @ViewChild('productSearchInput')
  productSearchInput!: ElementRef<HTMLInputElement>;

  @ViewChild('parentLicenseSearchInput')
  parentLicenseSearchInput!: ElementRef<HTMLInputElement>;

  @Input() desktopLicenses?: {
    active: DesktopLicenseWithProduct[];
    expired: DesktopLicenseWithProduct[];
    pending: DesktopLicenseWithProduct[];
  };
  @Input() tenant?: Tenant;

  @Output() licensesAdded = new EventEmitter<DesktopLicenseWithProduct[]>();

  products: Product[] | undefined;
  productVariants: { [prop: string]: ProductVariant[] } = {};

  loadingProducts = false;
  loadingVariants = false;
  productFilters = {
    searchText: '',
    productId: '',
    duration: '',
    priceLockLevel: '',
    deliveryMethod: '',
    showInactiveProducts: false,
  };

  addingLicense = false;
  errorAddingLicense = '';
  calculatingExpirationDate = true;
  errorGettingExpirationDate = '';

  licenseIntent: LicenseAddIntent | null = null;
  wizardStep: WizardStep = 'intent';
  private stepHistory: WizardStep[] = ['intent'];

  isSiteLicense?: boolean = undefined;
  isNewLicense = true;
  startDate: string = new Date().toISOString().slice(0, 10);
  expirationDate?: string;

  currentProduct: Product | undefined;
  currentVariant: ProductVariant | undefined;
  selectedParentLicenseId = '';
  selectedParentLicense?: DesktopLicenseWithProduct;

  showAllParentLicenses = false;
  parentLicenseSearchText = '';
  parentSelectMode: 'site' | 'addon' = 'addon';

  primaryLicense: QueuedLicense | null = null;
  additionalAddons: QueuedLicense[] = [];
  selectingAddonProducts = false;

  constructor(
    private licensesService: LicensesService,
    private productsService: ProductsService
  ) {}

  ngOnInit() {
    this.loadProducts();
  }

  get wizardTitle(): string {
    switch (this.wizardStep) {
      case 'intent':
        return 'Add License';
      case 'site-license-choice':
        return 'Site License';
      case 'parent-select':
        return this.parentSelectMode === 'site'
          ? 'Select Primary License'
          : 'Select License for Add-on';
      case 'product-select':
        return this.selectingAddonProducts
          ? 'Select New Add-on Product'
          : 'Select New Product';
      case 'license-config':
        return 'License Details';
      case 'more-addons':
        return 'Additional Add-ons';
      default:
        return 'Add License';
    }
  }

  get displayProducts() {
    let products = this.products;
    if (!products) return [];

    if (!this.productFilters.showInactiveProducts) {
      products = products?.filter((p) => p.active);
    }

    if (this.selectingAddonProducts || this.licenseIntent === 'addon') {
      products = products.filter((p) => p.requires_parent);
    } else if (this.licenseIntent === 'primary') {
      products = products.filter((p) => !p.requires_parent);
    }

    if (
      !this.productFilters.searchText ||
      this.productFilters.searchText === ''
    ) {
      return products;
    }

    const term = this.productFilters.searchText.trim().toLowerCase();
    return products.filter(
      (p) =>
        p.code.toLowerCase().includes(term) ||
        p.title.toLowerCase().includes(term) ||
        p.variance_code.toLowerCase().includes(term)
    );
  }

  get displayVariants() {
    const variants = this.productVariants[this.productFilters.productId];
    if (!variants) return [];

    let displayVariants = variants;

    displayVariants = displayVariants.filter((p) => {
      if (
        this.productFilters.duration !== '' &&
        p.duration_months !== +this.productFilters.duration
      ) {
        return false;
      }
      if (
        this.productFilters.priceLockLevel !== '' &&
        p.price_lock_level !== +this.productFilters.priceLockLevel
      ) {
        return false;
      }
      if (
        this.productFilters.deliveryMethod !== '' &&
        p.delivery_method !== this.productFilters.deliveryMethod
      ) {
        return false;
      }
      if (
        this.productFilters.priceLockLevel &&
        p.price_lock_level !== +this.productFilters.priceLockLevel
      ) {
        return false;
      }

      return true;
    });

    displayVariants.sort((a, b) => {
      if (a.duration_months !== b.duration_months)
        return a.duration_months - b.duration_months;
      if (a.price_lock_level !== b.price_lock_level)
        return a.price_lock_level - b.price_lock_level;
      return 0;
    });

    return displayVariants;
  }

  get variantDurations() {
    const variants = this.productVariants[this.productFilters.productId];
    if (!variants) return [];
    return variants
      .map((p) => p.duration_months)
      .reduce((acc, curr) => {
        if (acc.indexOf(curr) === -1) {
          acc.push(curr);
        }
        return acc;
      }, [] as number[])
      .sort((a, b) => a - b);
  }

  get priceLockLevels() {
    const variants = this.productVariants[this.productFilters.productId];
    if (!variants) return [];
    return this.productVariants[this.productFilters.productId]
      .map((p) => p.price_lock_level)
      .reduce((acc, curr) => {
        if (acc.indexOf(curr) === -1) {
          acc.push(curr);
        }
        return acc;
      }, [] as number[])
      .sort((a, b) => a - b);
  }

  get allNonExpiredLicenses(): DesktopLicenseWithProduct[] {
    if (!this.desktopLicenses) return [];
    return [...this.desktopLicenses.active, ...this.desktopLicenses.pending];
  }

  get defaultParentLicenses(): DesktopLicenseWithProduct[] {
    if (!this.desktopLicenses) return [];

    if (this.parentSelectMode === 'site') {
      return this.desktopLicenses.active.filter(
        (license) => !license.product.requires_parent
      );
    }

    return this.allNonExpiredLicenses.filter(
      (license) => !license.product.requires_parent
    );
  }

  get displayParentLicenses(): DesktopLicenseWithProduct[] {
    const licenses = this.showAllParentLicenses
      ? this.allNonExpiredLicenses
      : this.defaultParentLicenses;

    if (!this.parentLicenseSearchText.trim()) {
      return licenses;
    }

    const term = this.parentLicenseSearchText.trim().toLowerCase();
    return licenses.filter((license) => {
      const code = license.product.code.toLowerCase();
      const title = license.product.title.toLowerCase();
      const variance = license.product.variance_code.toLowerCase();
      return (
        code.includes(term) || title.includes(term) || variance.includes(term)
      );
    });
  }

  get queuedLicenses(): QueuedLicense[] {
    const queued: QueuedLicense[] = [];
    if (this.primaryLicense) {
      queued.push(this.primaryLicense);
    }
    queued.push(...this.additionalAddons);
    return queued;
  }

  /** Parent that additional add-ons attach to on the more-addons step. */
  get moreAddonsAttachmentTargetLabel(): string | null {
    if (this.licenseIntent === 'addon' && this.selectedParentLicense) {
      return this.formatLicenseLabel(this.selectedParentLicense);
    }
    if (this.licenseIntent === 'primary' && this.primaryLicense) {
      return this.formatQueuedLicenseLabel(this.primaryLicense);
    }
    return null;
  }

  get moreAddonsPrompt(): string {
    if (this.licenseIntent === 'primary') {
      return 'Add more add-on licenses to the new primary license above?';
    }
    return 'Add more add-on licenses to the same parent license?';
  }

  get canGoNext(): boolean {
    switch (this.wizardStep) {
      case 'intent':
        return this.licenseIntent !== null;
      case 'site-license-choice':
        return true;
      case 'parent-select':
        return this.selectedParentLicenseId !== '';
      case 'license-config':
        return (
          !!this.expirationDate &&
          !this.calculatingExpirationDate &&
          !this.errorGettingExpirationDate
        );
      case 'more-addons':
        return true;
      default:
        return false;
    }
  }

  get showBackButton(): boolean {
    return this.stepHistory.length > 1;
  }

  get showNextButton(): boolean {
    return (
      // this.wizardStep === 'intent' ||
      // this.wizardStep === 'site-license-choice' ||
      this.wizardStep === 'parent-select' ||
      this.wizardStep === 'license-config'
    );
  }

  loadProducts() {
    this.loadingProducts = true;
    this.productsService.getAllProducts().subscribe({
      next: (res) => {
        if (res.products) {
          this.products = res.products;
          this.products.sort((a, b) => a.code.localeCompare(b.code));
        } else {
          this.products = [];
        }
      },
      error: (err) => {
        console.error(err);
      },
      complete: () => {
        this.loadingProducts = false;
      },
    });
  }

  displayDeliveryMethod(deliveryMethod: string) {
    switch (deliveryMethod) {
      case 'XXX':
        return 'Physical';
      case 'DNL':
        return 'Download';
      default:
        return '';
    }
  }

  selectIntent(intent: LicenseAddIntent) {
    this.licenseIntent = intent;
    this.selectingAddonProducts = intent === 'addon';
    if (intent === 'addon') {
      this.parentSelectMode = 'addon';
    }
  }

  selectSiteLicenseChoice(isSite: boolean) {
    this.isSiteLicense = isSite;
  }

  selectParentLicense(license: DesktopLicenseWithProduct) {
    this.selectedParentLicense = license;
    this.selectedParentLicenseId = license.id;
    if (document.activeElement) {
      (document.activeElement as HTMLElement).blur();
    }
  }

  handlePrimaryVariantClick(variant: ProductVariant) {
    this.currentVariant = variant;
    this.applyInheritedDatesForConfig();
    this.calculateExpirationDate(
      new Date(this.startDate),
      variant.id,
      this.parentIdForExpiration()
    );
    this.goToStep('license-config');
  }

  handleAddonVariantClick(variant: ProductVariant) {
    if (!this.currentProduct) return;

    if (!this.primaryLicense) {
      this.currentVariant = variant;
      this.applyInheritedDatesForConfig();
      this.calculateExpirationDate(
        new Date(this.startDate),
        variant.id,
        this.parentIdForExpiration()
      );
      this.goToStep('license-config');
      return;
    }

    const ref = this.primaryLicense;
    this.additionalAddons.push({
      product: this.currentProduct,
      variant,
      isSiteLicense: false,
      isNewLicense: ref.isNewLicense,
      startDate: ref.startDate,
      expirationDate: ref.expirationDate,
    });
    this.resetProductSelection();
    this.goToStep('more-addons');
  }

  handleVariantClick(variant: ProductVariant) {
    if (this.selectingAddonProducts && this.primaryLicense) {
      this.handleAddonVariantClick(variant);
      return;
    }
    this.handlePrimaryVariantClick(variant);
  }

  applyInheritedDatesForConfig() {
    if (this.licenseIntent === 'addon' && this.selectedParentLicense) {
      const parent = this.selectedParentLicense;
      if (parent.start_date) {
        this.startDate = parent.start_date.slice(0, 10);
      } else {
        this.startDate = parent.created_at.slice(0, 10);
      }
      if (parent.expiration) {
        this.expirationDate = parent.expiration.slice(0, 10);
      }
      this.isNewLicense = parent.new_license;
      this.isSiteLicense = false;
      return;
    }

    if (this.primaryLicense) {
      this.startDate = this.primaryLicense.startDate;
      this.expirationDate = this.primaryLicense.expirationDate;
      this.isNewLicense = this.primaryLicense.isNewLicense;
    }
  }

  parentIdForExpiration(): string | undefined {
    if (this.licenseIntent === 'addon') {
      return this.selectedParentLicenseId || undefined;
    }
    if (this.isSiteLicense && this.selectedParentLicenseId) {
      return this.selectedParentLicenseId;
    }
    return undefined;
  }

  calculateExpirationDate(
    startDate: Date,
    variantId: string,
    parentLicenseId?: string
  ) {
    this.calculatingExpirationDate = true;
    this.errorGettingExpirationDate = '';
    if (parentLicenseId === '') {
      parentLicenseId = undefined;
    }

    this.licensesService
      .getPreemtiveExpirationDate(startDate, variantId, parentLicenseId)
      .subscribe({
        next: (res) => {
          this.calculatingExpirationDate = false;
          this.expirationDate = res.expiration_date.slice(0, 10);
        },
        error: (err) => {
          this.calculatingExpirationDate = false;
          console.error(err);
          this.errorGettingExpirationDate = err;
        },
      });
  }

  handleStartDateChange() {
    if (this.startDate && this.currentVariant) {
      this.calculateExpirationDate(
        new Date(this.startDate),
        this.currentVariant.id,
        this.parentIdForExpiration()
      );
    }
  }

  handleProductChange(product: Product) {
    this.currentProduct = product;
    this.productFilters.productId = product.id;
    this.productFilters.duration = '';
    this.productFilters.priceLockLevel = '';
    this.productFilters.deliveryMethod = 'DNL';

    this.loadProductVariants(product.id);

    if (document.activeElement) {
      (document.activeElement as HTMLElement).blur();
    }
  }

  findProductMatchingLicense(
    license: DesktopLicenseWithProduct
  ): Product | undefined {
    if (!this.products) return undefined;

    return (
      this.products.find((p) => p.id === license.product.id) ??
      this.products.find(
        (p) =>
          p.code === license.product.code &&
          p.variance_code === license.product.variance_code
      )
    );
  }

  /** Sets the currently selected product and variant to the product and variant
  of the selected new primary site license. */
  applyProductSelectionFromSiteParent() {
    const parent = this.selectedParentLicense;
    if (!parent || this.licenseIntent !== 'primary' || !this.isSiteLicense) {
      return;
    }

    const matchingProduct = this.findProductMatchingLicense(parent);
    if (!matchingProduct) return;

    this.currentProduct = matchingProduct;
    this.productFilters.productId = matchingProduct.id;
    this.loadProductVariants(matchingProduct.id, parent.product_variant);
  }

  loadProductVariants(productId: string, matchVariant?: ProductVariant) {
    if (this.productVariants[productId] === undefined) {
      this.loadingVariants = true;

      this.productsService.getProductDetails(productId).subscribe({
        next: (res) => {
          if (res.product_with_variants) {
            this.productVariants[productId] =
              res.product_with_variants.variants;
            if (matchVariant) {
              this.applyFiltersFromVariant(matchVariant);
            } else {
              this.setInitialPriceLockLevel(productId);
            }
          }
        },
        error: (err) => {
          console.error(err);
        },
        complete: () => {
          this.loadingVariants = false;
        },
      });
    } else {
      if (matchVariant) {
        this.applyFiltersFromVariant(matchVariant);
      } else {
        this.setInitialPriceLockLevel(productId);
      }
    }
  }

  /** Sets the current product selection filters to the values of the given variant. */
  applyFiltersFromVariant(variant: ProductVariant) {
    this.productFilters.duration = String(variant.duration_months);
    this.productFilters.priceLockLevel = String(variant.price_lock_level);
    this.productFilters.deliveryMethod = variant.delivery_method || 'DNL';
  }

  setInitialPriceLockLevel(productId: string) {
    let highest = -Infinity;
    for (const variant of this.productVariants[productId]) {
      if (variant.price_lock_level > highest) {
        highest = variant.price_lock_level;
      }
    }
    this.productFilters.priceLockLevel = highest.toString();
  }

  focusProductSearchInput() {
    this.productSearchInput.nativeElement.focus();
  }

  focusParentLicenseSearchInput() {
    this.parentLicenseSearchInput.nativeElement.focus();
  }

  chooseMoreAddons(addMore: boolean) {
    if (addMore) {
      this.selectingAddonProducts = true;
      this.resetProductSelection();
      this.goToStep('product-select');
      return;
    }
    this.submitLicenses();
  }

  goNext() {
    switch (this.wizardStep) {
      case 'intent':
        if (this.licenseIntent === 'primary') {
          this.goToStep('site-license-choice');
        } else {
          this.parentSelectMode = 'addon';
          this.goToStep('parent-select');
        }
        break;
      case 'site-license-choice':
        if (this.isSiteLicense) {
          this.parentSelectMode = 'site';
          this.goToStep('parent-select');
        } else {
          this.selectingAddonProducts = false;
          this.goToStep('product-select');
        }
        break;
      case 'parent-select':
        this.selectingAddonProducts = this.licenseIntent === 'addon';
        this.goToStep('product-select');
        if (this.licenseIntent === 'primary' && this.isSiteLicense) {
          this.applyProductSelectionFromSiteParent();
        }
        break;
      case 'license-config':
        this.savePrimaryLicenseFromConfig();
        this.goToStep('more-addons');
        break;
    }
  }

  goBack() {
    if (this.stepHistory.length <= 1) return;

    const leaving = this.wizardStep;
    this.stepHistory.pop();
    this.wizardStep = this.stepHistory[this.stepHistory.length - 1];

    if (leaving === 'more-addons' && this.additionalAddons.length > 0) {
      this.additionalAddons.pop();
    }
    if (leaving === 'license-config') {
      this.primaryLicense = null;
      this.currentVariant = undefined;
    }
    if (leaving === 'product-select' && !this.primaryLicense) {
      this.resetProductSelection();
    }
  }

  savePrimaryLicenseFromConfig() {
    if (!this.currentProduct || !this.currentVariant || !this.expirationDate) {
      return;
    }

    if (this.isSiteLicense === undefined) {
      this.isSiteLicense = false;
    }

    this.primaryLicense = {
      product: this.currentProduct,
      variant: this.currentVariant,
      isSiteLicense: this.isSiteLicense,
      isNewLicense: this.isNewLicense,
      startDate: this.startDate,
      expirationDate: this.expirationDate,
    };
  }

  toLicenseDetails(
    queued: QueuedLicense,
    parentDesktopLicenseId?: string
  ): NewDesktopLicenseDetails {
    const details: NewDesktopLicenseDetails = {
      product_variant_id: queued.variant.id,
      start_date: new Date(queued.startDate),
      expiration: new Date(queued.expirationDate),
      new_license: queued.isNewLicense,
      is_site_license: queued.isSiteLicense,
    };

    if (parentDesktopLicenseId) {
      details.parent_desktop_license_id = parentDesktopLicenseId;
    }

    return details;
  }

  buildCreateRequest(): CreateDesktopLicenseRequest {
    const tenant = this.tenant;
    if (!tenant) {
      throw new Error('Missing tenant');
    }

    if (!this.primaryLicense) {
      throw new Error('No license selected');
    }

    if (this.licenseIntent === 'addon') {
      if (!this.selectedParentLicenseId) {
        throw new Error('Missing parent license for add-ons');
      }

      const addOnLicenses = [this.primaryLicense, ...this.additionalAddons].map(
        (queued) => this.toLicenseDetails(queued, this.selectedParentLicenseId)
      );

      return {
        tenant_id: tenant.id,
        creation_intent: 'add_ons',
        add_ons_parent_license_id: this.selectedParentLicenseId,
        add_on_licenses: addOnLicenses,
        // TODO: Remove this once parent license check is enabled
        skip_parent_license_check: true,
        // skip_parent_license_check: false,
      };
    }

    const primaryParentId =
      this.primaryLicense.isSiteLicense && this.selectedParentLicenseId
        ? this.selectedParentLicenseId
        : undefined;

    return {
      tenant_id: tenant.id,
      creation_intent: 'new_primary',
      new_primary_license: this.toLicenseDetails(
        this.primaryLicense,
        primaryParentId
      ),
      add_on_licenses: this.additionalAddons.map((queued) =>
        this.toLicenseDetails(queued)
      ),
      // TODO: Remove this once parent license check is enabled
      skip_parent_license_check: true,
      //skip_parent_license_check: !primaryParentId,
    };
  }

  submitLicenses() {
    if (!this.primaryLicense) {
      this.errorAddingLicense = 'No license selected';
      return;
    }

    if (!this.tenant) {
      this.errorAddingLicense = 'Missing tenant';
      return;
    }

    this.addingLicense = true;
    this.errorAddingLicense = '';

    let request: CreateDesktopLicenseRequest;
    try {
      request = this.buildCreateRequest();
    } catch (err) {
      this.addingLicense = false;
      this.errorAddingLicense =
        err instanceof Error ? err.message : 'Invalid license request';
      return;
    }

    this.licensesService.createDesktopLicenses(request).subscribe({
      next: (res) => {
        this.addingLicense = false;
        const created = this.licensesService.collectCreatedLicenses(res);
        if (created.length > 0) {
          this.licensesAdded.emit(created);
          this.resetWizard();
        } else {
          this.errorAddingLicense = 'Server error adding licenses';
        }
      },
      error: (err) => {
        this.addingLicense = false;
        this.errorAddingLicense = err;
      },
    });
  }

  goToStep(step: WizardStep) {
    this.wizardStep = step;
    this.stepHistory.push(step);
  }

  resetProductSelection() {
    this.currentProduct = undefined;
    this.currentVariant = undefined;
    this.productFilters = {
      searchText: '',
      productId: '',
      duration: '',
      priceLockLevel: '',
      deliveryMethod: '',
      showInactiveProducts: false,
    };
  }

  resetWizard() {
    this.wizardStep = 'intent';
    this.stepHistory = ['intent'];
    this.licenseIntent = null;
    this.isSiteLicense = false;
    this.isNewLicense = true;
    this.startDate = new Date().toISOString().slice(0, 10);
    this.expirationDate = undefined;
    this.selectedParentLicenseId = '';
    this.selectedParentLicense = undefined;
    this.showAllParentLicenses = false;
    this.parentLicenseSearchText = '';
    this.primaryLicense = null;
    this.additionalAddons = [];
    this.selectingAddonProducts = false;
    this.addingLicense = false;
    this.errorAddingLicense = '';
    this.calculatingExpirationDate = true;
    this.errorGettingExpirationDate = '';
    this.resetProductSelection();
  }

  clearSelectedValues() {
    this.resetWizard();
  }

  formatLicenseLabel(license: DesktopLicenseWithProduct): string {
    const site = license.site_license ? ' [Site]' : '';
    return (
      `${license.product.code} - ${license.product.title} ` +
      `(${license.product.variance_code})${site}`
    );
  }

  formatQueuedLicenseLabel(queued: QueuedLicense): string {
    const site = queued.isSiteLicense ? ' [Site]' : '';
    return (
      `${queued.product.code} - ${queued.product.title} ` +
      `(${queued.product.variance_code})${site}`
    );
  }
}
